@file:Suppress("UnsafeCastFromDynamic", "unused")

package com.zi.support.web

import org.w3c.files.File
import kotlin.js.Promise
import kotlinx.browser.window

// A named second Firebase App instance (rather than reusing the default one) so the
// customer portal's Auth session lives under its own storage key and never fires
// onAuthStateChanged for - or gets signed out by - the agent app's session, even when
// both are open as tabs of the same browser origin at once.
internal class FirebaseClient(appName: String?) {
    private val appModule: dynamic = js("require('firebase/app')")
    private val authModule: dynamic = js("require('firebase/auth')")
    internal val firestore: dynamic = js("require('firebase/firestore')")
    internal val storageModule: dynamic = js("require('firebase/storage')")
    private val functionsModule: dynamic = js("require('firebase/functions')")

    private val defaultConfig: dynamic = json(
        "projectId" to "supportapp-f4c42",
        "appId" to "1:999429780571:web:4412400e55a01545bc3b7e",
        "storageBucket" to "supportapp-f4c42.firebasestorage.app",
        "apiKey" to "AIzaSyCJw_PmAkqp30FIn16rpO8J0GvZZcMaOB8",
        "authDomain" to "supportapp-f4c42.firebaseapp.com",
        "messagingSenderId" to "999429780571",
        "measurementId" to "G-G1YBSHE6B6"
    )
    // A client-specific deployment can inject this object before the app bundle
    // loads; the existing SupportApp project remains the safe default.
    private val runtimeConfig: dynamic = js("globalThis.__SUPPORTAPP_FIREBASE_CONFIG__")
    private val config: dynamic = if (runtimeConfig == null) defaultConfig else runtimeConfig

    val app: dynamic = if (appName == null) appModule.initializeApp(config) else appModule.initializeApp(config, appName)
    val auth: dynamic = authModule.getAuth(app)
    // Staff app: keep Firestore data in IndexedDB, so coming back to a page (or reloading) within ~30 minutes
    // is billed only for documents that changed, not the whole list again. The customer portal keeps the
    // default in-memory cache since customers may be on shared computers. Falls back to memory if the
    // browser refuses IndexedDB (e.g. some private windows).
    private val persistent = appName == null
    val db: dynamic = if (!persistent) firestore.getFirestore(app) else try {
        firestore.initializeFirestore(app, json("localCache" to firestore.persistentLocalCache(json("tabManager" to firestore.persistentMultipleTabManager()))))
    } catch (_: Throwable) { firestore.getFirestore(app) }
    val storage: dynamic = storageModule.getStorage(app)
    private val functions: dynamic = functionsModule.getFunctions(app, "africa-south1")

    fun observeAuth(block: (dynamic) -> Unit): dynamic = authModule.onAuthStateChanged(auth, block)

    fun login(username: String, password: String): dynamic {
        val clean = username.trim()
        val email = if (clean.contains("@")) clean.lowercase()
        else clean.lowercase().replace(Regex("[^a-z0-9]"), "") + "@supportapp.internal"
        return authModule.signInWithEmailAndPassword(auth, email, password)
    }

    // Staff logout also wipes the on-disk cache (it holds tickets and chats) and reloads, so a shared
    // computer keeps nothing behind.
    fun logout(): dynamic = authModule.signOut(auth).then {
        if (persistent) firestore.terminate(db).then { firestore.clearIndexedDbPersistence(db) }.then({ window.location.reload() }, { window.location.reload() })
    }
    fun loginEphemeral(token: String): dynamic = authModule.setPersistence(auth, authModule.inMemoryPersistence).then {
        authModule.signInWithCustomToken(auth, token)
    }
    fun loginWithCustomToken(token: String): dynamic = authModule.signInWithCustomToken(auth, token)
    fun currentUser(): dynamic = auth.currentUser
    // Reads custom claims (e.g. the customer portal's customerEmail claim) off
    // a fresh ID token. The `true` forces a refresh so a claim minted moments
    // ago by createCustomToken is not missed by a cached token.
    fun idTokenClaims(user: dynamic): dynamic = authModule.getIdTokenResult(user, true)
    /**
     * Restarts the connection to Firestore. Re-listening alone can keep showing the cached copy when the browser's
     * connection went quiet (sleep, a network change, a long-hidden tab); after a restart the server sends what changed.
     * Resolves either way, so a refresh never gets stuck.
     */
    fun reconnect(): dynamic = firestore.disableNetwork(db).then { firestore.enableNetwork(db) }.catch { _: dynamic -> null }
    fun serverTimestamp(): dynamic = firestore.serverTimestamp()
    fun timestampFromMillis(value: Double): dynamic = firestore.Timestamp.fromMillis(value)
    fun increment(value: Int): dynamic = firestore.increment(value)
    fun arrayUnion(value: String): dynamic = firestore.arrayUnion(value)
    fun arrayUnionAll(values: List<String>): dynamic = firestore.arrayUnion.apply(null, values.toTypedArray())
    fun arrayRemoveAll(values: List<String>): dynamic = firestore.arrayRemove.apply(null, values.toTypedArray())

    fun collection(path: String): dynamic {
        return firestore.collection(db, path)
    }

    fun document(path: String): dynamic {
        return firestore.doc(db, path)
    }

    fun listenCollection(
        path: String,
        constraints: Array<dynamic> = emptyArray(),
        onRows: (List<WebRecord>) -> Unit,
        onError: (String) -> Unit
    ): dynamic {
        val base = collection(path)
        val query = if (constraints.isEmpty()) base else firestore.query.apply(null, arrayOf<dynamic>(base) + constraints)
        return firestore.onSnapshot(query, { snapshot: dynamic ->
            val rows = mutableListOf<WebRecord>()
            val docs: dynamic = snapshot.docs
            for (index in 0 until (docs.length as Int)) {
                val doc = docs[index]
                rows += WebRecord(doc.id as String, doc.ref.path as String, doc.data(json("serverTimestamps" to "estimate")))
            }
            onRows(rows)
        }, { error: dynamic -> onError(errorMessage(error)) })
    }

    fun listenQuery(query: dynamic, onRows: (List<WebRecord>) -> Unit, onError: (String) -> Unit): dynamic =
        firestore.onSnapshot(query, { snapshot: dynamic ->
            val rows = mutableListOf<WebRecord>()
            val docs: dynamic = snapshot.docs
            for (index in 0 until (docs.length as Int)) {
                val doc = docs[index]
                rows += WebRecord(doc.id as String, doc.ref.path as String, doc.data(json("serverTimestamps" to "estimate")))
            }
            onRows(rows)
        }, { error: dynamic -> onError(errorMessage(error)) })

    fun getCollection(path: String, constraints: Array<dynamic> = emptyArray()): dynamic {
        val base = collection(path)
        val query = if (constraints.isEmpty()) base else firestore.query.apply(null, arrayOf<dynamic>(base) + constraints)
        return firestore.getDocs(query)
    }

    fun getCollectionGroup(name: String, constraints: Array<dynamic> = emptyArray()): dynamic {
        val base = firestore.collectionGroup(db, name)
        val query = if (constraints.isEmpty()) base else firestore.query.apply(null, arrayOf<dynamic>(base) + constraints)
        return firestore.getDocs(query)
    }

    fun getDocument(path: String): dynamic = firestore.getDoc(document(path))
    fun add(path: String, data: dynamic): dynamic = firestore.addDoc(collection(path), data)
    fun set(path: String, data: dynamic, merge: Boolean = false): dynamic =
        if (merge) firestore.setDoc(document(path), data, json("merge" to true)) else firestore.setDoc(document(path), data)
    fun update(path: String, data: dynamic): dynamic = firestore.updateDoc(document(path), data)
    /** Writes [data] to [to] and deletes [from] in one batch: both happen or neither does. */
    fun move(from: String, to: String, data: dynamic): dynamic {
        // No .let here: on a dynamic it compiles to a JS method call (batch.let) and throws.
        val batch = firestore.writeBatch(db)
        batch.set(document(to), data); batch.delete(document(from))
        return batch.commit()
    }
    fun delete(path: String): dynamic = firestore.deleteDoc(document(path))
    fun deleteStorage(path: String): dynamic = storageModule.deleteObject(storageModule.ref(storage, path))

    fun callable(name: String, payload: dynamic = json()): dynamic =
        functionsModule.httpsCallable(functions, name)(payload)

    fun upload(path: String, file: File, contentType: String = file.type): dynamic {
        val reference = storageModule.ref(storage, path)
        val metadata = json("contentType" to contentType, "customMetadata" to json("ownerUid" to (currentUser()?.uid ?: "")))
        return storageModule.uploadBytes(reference, file, metadata).then { result: dynamic ->
            storageModule.getDownloadURL(result.ref).then { url: dynamic ->
                json("url" to (url as String), "path" to path)
            }
        }
    }

    fun uploadWithProgress(path: String, file: File, onProgress: (Int) -> Unit): dynamic {
        val reference = storageModule.ref(storage, path)
        val metadata = json("contentType" to file.type, "customMetadata" to json("ownerUid" to (currentUser()?.uid ?: "")))
        return Promise<dynamic> { resolve, reject ->
            val task = storageModule.uploadBytesResumable(reference, file, metadata)
            task.on("state_changed", { snapshot: dynamic ->
                val total=(snapshot.totalBytes as? Number)?.toDouble() ?: 0.0
                val sent=(snapshot.bytesTransferred as? Number)?.toDouble() ?: 0.0
                onProgress(if(total<=0) 0 else ((sent/total)*100).toInt().coerceIn(0,100))
            }, { error: dynamic -> reject(error) }, {
                storageModule.getDownloadURL(task.snapshot.ref).then { url: dynamic -> resolve(json("url" to (url as String), "path" to path)) }.catch { error: dynamic -> reject(error) }
            })
        }
    }

    fun uploadBlob(path: String, blob: dynamic, contentType: String): dynamic {
        val reference = storageModule.ref(storage, path)
        val metadata = json("contentType" to contentType, "customMetadata" to json("ownerUid" to (currentUser()?.uid ?: "")))
        return storageModule.uploadBytes(reference, blob, metadata).then { result: dynamic ->
            storageModule.getDownloadURL(result.ref).then { url: dynamic -> json("url" to (url as String), "path" to path) }
        }
    }

    fun nextDocumentNumber(kind: String): dynamic {
        val reference = document("documentCounters/$kind")
        return firestore.runTransaction(db) { transaction: dynamic ->
            transaction.get(reference).then { snapshot: dynamic ->
                val current = if (snapshot.exists()) (snapshot.data().lastNumber as? Number)?.toInt() ?: 0 else 0
                val next = current + 1
                transaction.set(reference, json("lastNumber" to next, "updatedAt" to serverTimestamp()))
                val prefix = when (kind) { "checklists" -> "CL-"; "siteVisits" -> "SV-"; else -> "TKT" }
                prefix + next.toString().padStart(3, '0')
            }
        }
    }

    fun errorMessage(error: dynamic): String =
        (error?.message as? String)?.substringAfter(": ")?.ifBlank { null }
            ?: (error?.message as? String)
            ?: "The request could not be completed."
}

internal val FirebaseGateway = FirebaseClient(if (MobileInsights.enabled) "mobile-insights" else null)
internal val CustomerFirebaseGateway = FirebaseClient("customer")

internal data class WebRecord(val id: String, val path: String, val data: dynamic)

internal fun json(vararg pairs: Pair<String, Any?>): dynamic {
    val value: dynamic = js("({})")
    pairs.forEach { (key, item) -> value[key] = item }
    return value
}

internal fun promise(promise: dynamic, success: (dynamic) -> Unit, failure: (String) -> Unit = { toast(it, true) }) {
    promise.then({ value: dynamic -> success(value) }).catch({ error: dynamic -> failure(FirebaseGateway.errorMessage(error)) })
}
